/*
 * ProfileCompletionForm Component - Complete user profile after registration
 * 
 * This component is shown to new users to complete their profile with:
 * - Full Name (mandatory for both coaches and athletes)
 * - Birth Date (optional for coaches, mandatory for athletes)
 * 
 * Matches the current platform styling and UX patterns.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { Calendar, Loader2, User } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useProfile } from '../../contexts/ProfileContext';
import { useI18n } from '../../lib/i18n/context';
import { completeProfile } from '../../lib/supabase';

// Validation schema
const profileCompletionSchema = z.object({
  full_name: z.string()
    .min(2, 'full-name-validation-min')
    .max(100, 'full-name-validation-max')
    .regex(/^[a-zA-Z\s'-]+$/, 'full-name-validation-format'),
  birth_date: z.string().optional()
}).refine((data) => {
  // If birth_date is provided, validate it's a reasonable date
  if (data.birth_date) {
    const date = new Date(data.birth_date);
    const now = new Date();
    const minDate = new Date(now.getFullYear() - 100, 0, 1); // 100 years ago
    const maxDate = new Date(now.getFullYear() - 13, now.getMonth(), now.getDate()); // At least 13 years old
    
    return date >= minDate && date <= maxDate;
  }
  return true;
}, {
  message: "birth-date-validation",
  path: ["birth_date"]
});

type ProfileCompletionData = z.infer<typeof profileCompletionSchema>;

interface ProfileCompletionFormProps {
  onComplete: () => void;
}

export function ProfileCompletionForm({ onComplete }: ProfileCompletionFormProps) {
  const { t } = useI18n();
  const { profile } = useProfile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAthlete = profile?.role === 'athlete';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ProfileCompletionData>({
    resolver: zodResolver(profileCompletionSchema),
    defaultValues: {
      full_name: profile?.full_name || '',
      birth_date: profile?.birth_date || ''
    }
  });

  const onSubmit = async (data: ProfileCompletionData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // For athletes, birth_date is required
      if (isAthlete && !data.birth_date) {
        setError(t('birth-date-required-for-athletes'));
        setIsSubmitting(false);
        return;
      }

      const { error: updateError } = await completeProfile({
        full_name: data.full_name,
        birth_date: data.birth_date
      });

      if (updateError) {
        setError(updateError.message || 'Failed to complete profile. Please try again.');
        return;
      }

      // Success! Call the completion callback
      onComplete();
    } catch (err: any) {
      console.error('Error completing profile:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-white dark:from-[#161616] dark:to-[#1A1A1A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                <User className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t('complete-your-profile')}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isAthlete ? t('athlete-profile-setup') : t('coach-profile-setup')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            {/* Full Name Field */}
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('full-name')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="full_name"
                  type="text"
                  placeholder={isAthlete ? t('full-name-placeholder') : t('full-name-placeholder-coach')}
                  {...register('full_name')}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {errors.full_name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {t(errors.full_name.message as string)}
                </p>
              )}
            </div>

            {/* Birth Date Field */}
            <div>
              <label htmlFor="birth_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('birth-date')} {isAthlete && <span className="text-red-500">*</span>}
                {!isAthlete && <span className="text-gray-500 text-xs">{t('birth-date-optional')}</span>}
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="birth_date"
                  type="date"
                  {...register('birth_date')}
                  max={new Date(new Date().getFullYear() - 13, new Date().getMonth(), new Date().getDate()).toISOString().split('T')[0]}
                  min={new Date(new Date().getFullYear() - 100, 0, 1).toISOString().split('T')[0]}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {errors.birth_date && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {t(errors.birth_date.message as string)}
                </p>
              )}
              {isAthlete && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('birth-date-required-athletes')}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('completing-profile')}
                </>
              ) : (
                t('complete-profile')
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('profile-completion-help')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 